import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { Mail, Lock, User, ArrowRight } from 'lucide-react'
import { createClient } from '@supabase/supabase-js'
import { API_CONFIG } from '../../../config/api-keys.js'

const supabase = createClient(
  API_CONFIG.supabase.url,
  API_CONFIG.supabase.anonKey
)

export default function Login() {
  const navigate = useNavigate()
  const [mode, setMode] = useState('login') // 'login' or 'signup'
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  // Check if already logged in
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        console.log('[Login] User already logged in, redirecting to pricing')
        navigate('/pricing')
      }
    })
  }, [navigate])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccessMessage('')
    setLoading(true)

    try {
      if (mode === 'signup') {
        // Sign up
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: name
            }
          }
        })

        if (error) throw error

        if (data.user && data.session) {
          console.log('[Login] Signup successful:', data.user.email)

          // Notify extension about new session
          window.postMessage({
            type: 'SUPABASE_AUTH_UPDATE',
            session: data.session
          }, '*')

          setSuccessMessage('Account created! Redirecting to pricing...')
          setTimeout(() => {
            navigate('/pricing')
          }, 1500)
        }
      } else {
        // Login
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password
        })

        if (error) throw error

        if (data.user && data.session) {
          console.log('[Login] Login successful:', data.user.email)

          // Notify extension about new session
          window.postMessage({
            type: 'SUPABASE_AUTH_UPDATE',
            session: data.session
          }, '*')

          setSuccessMessage('Welcome back! Redirecting...')
          setTimeout(() => {
            navigate('/pricing')
          }, 1000)
        }
      }
    } catch (err) {
      console.error('[Login] Error:', err)
      setError(err.message || 'Authentication failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const switchMode = () => {
    setMode(mode === 'login' ? 'signup' : 'login')
    setError('')
    setSuccessMessage('')
  }

  return (
    <>
      <Helmet>
        <title>{mode === 'login' ? 'Sign In' : 'Sign Up'} - HireFlow</title>
        <meta name="description" content="Sign in to HireFlow to manage your subscriptions and access premium features" />
      </Helmet>

      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          {/* Logo/Brand */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              HireFlow
            </h1>
            <p className="mt-2 text-neutral-600">
              Your AI-Powered Career Assistant
            </p>
          </div>

          {/* Auth Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {/* Header */}
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-neutral-900 mb-2">
                {mode === 'login' ? 'Welcome Back' : 'Create Account'}
              </h2>
              <p className="text-neutral-600">
                {mode === 'login'
                  ? 'Sign in to access your account'
                  : 'Join HireFlow and supercharge your job search'}
              </p>
            </div>

            {/* Success message */}
            {successMessage && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                {successMessage}
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'signup' && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    <User className="w-4 h-4 inline mr-2" />
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                    placeholder="John Doe"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  <Mail className="w-4 h-4 inline mr-2" />
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  <Lock className="w-4 h-4 inline mr-2" />
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                  placeholder="••••••••"
                />
                {mode === 'signup' && (
                  <p className="text-xs text-neutral-500 mt-1">
                    Minimum 6 characters
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading
                  ? 'Please wait...'
                  : mode === 'login'
                  ? 'Sign In'
                  : 'Create Account'}
                {!loading && <ArrowRight className="w-4 h-4" />}
              </button>
            </form>

            {/* Switch mode */}
            <div className="mt-6 text-center">
              <p className="text-neutral-600">
                {mode === 'login'
                  ? "Don't have an account? "
                  : 'Already have an account? '}
                <button
                  onClick={switchMode}
                  className="text-blue-600 hover:text-blue-700 font-semibold"
                >
                  {mode === 'login' ? 'Sign Up' : 'Sign In'}
                </button>
              </p>
            </div>

            {/* Extension note */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-900">
                <strong>Note:</strong> Once you sign in here, your session will be automatically available in the HireFlow Chrome Extension.
              </p>
            </div>
          </div>

          {/* Footer link */}
          <div className="mt-6 text-center">
            <a
              href="/pricing"
              className="text-neutral-600 hover:text-neutral-900 text-sm"
            >
              View Pricing Plans →
            </a>
          </div>
        </div>
      </div>
    </>
  )
}
